import { Combobox, ErrorLabel } from '@myosh/odin-components';
import { QueryClient, QueryClientProvider, useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/user.context';
import { usersInfiniteQueryOptions } from '../helpers/get-users';

const SEARCH_DEBOUNCE_MS = 300;

// Module-scoped so every render site shares one cache.
const queryClient = new QueryClient();

interface ControlledUserPickerProps {
  value: string | null;
  onValueChange: (value: string | null) => void;
  /** Overrides `state.tokens.id_token`. Login flow passes it explicitly, and in-app callers read from context. */
  idToken?: string;
  /** Overrides `state.user.currentSchema`. Login flow passes the in-progress schema, and in-app callers read from context. */
  schema?: string;
  error?: string;
}

const ControlledUserPickerContent = ({
  value,
  onValueChange,
  idToken: idTokenProp,
  schema: schemaProp,
  error,
}: ControlledUserPickerProps) => {
  const { t } = useTranslation();
  const { state } = useUser();
  const idToken = idTokenProp ?? state.tokens?.id_token;
  const schema = schemaProp ?? state.user?.currentSchema;

  const [query, setQuery] = useState(value ?? '');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Skipping the initial run keeps the seeded label out of the server filter until the user edits.
  const skipInitialDebounce = useRef(true);
  useEffect(() => {
    if (skipInitialDebounce.current) {
      skipInitialDebounce.current = false;
      return;
    }
    const id = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  const {
    data: users,
    isFetching: isUsersLoading,
    hasNextPage,
    fetchNextPage,
    isError: isUsersError,
  } = useInfiniteQuery({
    ...usersInfiniteQueryOptions(idToken ?? '', schema, debouncedQuery),
    enabled: Boolean(idToken && schema),
    select: (data) => data.pages.flatMap((p) => p.items ?? []).map((u) => u.userId),
  });

  const effectiveError = error ?? (isUsersError ? t(($) => $.controlUserLoadFailed) : undefined);

  return (
    <div className="flex flex-col">
      <Combobox
        items={users ?? []}
        value={value}
        onValueChange={onValueChange}
        getKey={(user) => user}
        getLabel={(user) => user}
        placeholder={t(($) => $.controlUserSearchPlaceholder)}
        isLoading={isUsersLoading}
        hasMore={hasNextPage}
        onLoadMore={fetchNextPage}
        query={query}
        onQueryChange={setQuery}
        showClear
        aria-invalid={effectiveError !== undefined ? true : undefined}
      />
      {effectiveError && <ErrorLabel>{effectiveError}</ErrorLabel>}
    </div>
  );
};

export function ControlledUserPicker(props: ControlledUserPickerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <ControlledUserPickerContent {...props} />
    </QueryClientProvider>
  );
}
